// apps/web/src/lib/validators/onboarding.ts

import { z } from "zod";
// FIX: Changed the import source for Gender to use the shared types package
import { Gender } from "@logistics-platform/types"; 

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MIN_FILE_SIZE = 10 * 1024; // 10KB (to avoid empty/corrupt files)
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

// Helper to validate image dimensions
const validateImageDimensions = (file: File): Promise<boolean> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      // Minimum 200x200, maximum 4000x4000
      const isValid = img.width >= 200 && img.height >= 200 && img.width <= 4000 && img.height <= 4000;
      resolve(isValid);
    };
    img.onerror = () => resolve(false);
    img.src = URL.createObjectURL(file);
  });
};

export const VerificationFormSchema = z.object({
  gender: z.nativeEnum(Gender, { 
    required_error: "Please select your gender.",
    invalid_type_error: "Please select a valid gender option."
  }),
  
  currentResidence: z
    .string()
    .min(10, "Please provide a more detailed address (at least 10 characters).")
    .max(200, "Address is too long. Please keep it under 200 characters.")
    .regex(/^[a-zA-Z0-9\s,.-]+$/, "Address contains invalid characters."),
  
  organizationName: z
    .string()
    .max(100, "Organization name is too long.")
    .optional(),
  
  nationalIdFile: z
    .any()
    .refine((files) => files?.length === 1, "National ID image is required.")
    .refine(
      (files) => files?.[0]?.size >= MIN_FILE_SIZE,
      "Image file is too small. Please upload a clear photo."
    )
    .refine(
      (files) => files?.[0]?.size <= MAX_FILE_SIZE,
      "Image file is too large. Maximum size is 5MB."
    )
    .refine(
      (files) => ACCEPTED_IMAGE_TYPES.includes(files?.[0]?.type),
      "Only .jpg, .jpeg, .png and .webp files are accepted."
    ),

  profilePictureFile: z
    .any()
    .refine((files) => files?.length === 1, "A profile picture is required.")
    .refine(
      (files) => files?.[0]?.size >= MIN_FILE_SIZE,
      "Image file is too small. Please upload a clear photo."
    )
    .refine(
      (files) => files?.[0]?.size <= MAX_FILE_SIZE,
      "Image file is too large. Maximum size is 5MB."
    )
    .refine(
      (files) => ACCEPTED_IMAGE_TYPES.includes(files?.[0]?.type),
      "Only .jpg, .jpeg, .png and .webp files are accepted."
    ),
});

export type TVerificationFormSchema = z.infer<typeof VerificationFormSchema>;